export const FormStates = {
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  VERIFY_PHONE: 'VERIFY_PHONE',
  DISCLOSURE: 'DISCLOSURE',
  PERSONAL_INFO: 'PERSONAL_INFO',
  OCCUPATION: 'OCCUPATION',
  ADDITIONAL_INFO: 'ADDITIONAL_INFO',
  ADDITIONAL_INFO_PAGE2: 'ADDITIONAL_INFO_PAGE2',
  FRAUD_WARNING_FOLLOW_UP: 'FRAUD_WARNING_FOLLOW_UP',
  KYC_GENERATE_QUESTIONS: 'KYC_GENERATE_QUESTIONS',
  KYC_REFRESH: 'KYC_REFRESH',
  COMPLETE: 'COMPLETE',
} as const

export type StateValue = (typeof FormStates)[keyof typeof FormStates]
type TransitionState = Exclude<StateValue, typeof FormStates.COMPLETE>

export const STEP_TO_PATH: Record<StateValue, string> = {
  [FormStates.EMAIL]: '/SignupEmail',
  [FormStates.PHONE]: '/SignupPhone',
  [FormStates.VERIFY_PHONE]: '/SignupVerifySMS',
  [FormStates.DISCLOSURE]: '/SignupDisclosure',
  [FormStates.PERSONAL_INFO]: '/SignupUserInfo',
  [FormStates.OCCUPATION]: '/SignupOccupation',
  [FormStates.ADDITIONAL_INFO]: '/SignupAdditionalInfo',
  [FormStates.ADDITIONAL_INFO_PAGE2]: '/SignupAdditionalInformation2',
  [FormStates.FRAUD_WARNING_FOLLOW_UP]: '/SignupFraudWarningFollowUp',
  [FormStates.KYC_GENERATE_QUESTIONS]: '/SignupKYC',
  [FormStates.KYC_REFRESH]: '/SignupKycRefresh',
  [FormStates.COMPLETE]: '/Complete',
}

export const PATH_TO_STEP: Record<string, StateValue> = Object.fromEntries(
  Object.entries(STEP_TO_PATH).map(([step, path]) => [path, step as StateValue])
)

export class FormWizard {
  private currentState: StateValue
  private transitions: Record<TransitionState, StateValue>
  private reverseTransitions: Partial<Record<StateValue, StateValue>>

  constructor() {
    this.currentState = FormStates.EMAIL
    this.transitions = {
      [FormStates.EMAIL]: FormStates.PHONE,
      [FormStates.PHONE]: FormStates.VERIFY_PHONE,
      [FormStates.VERIFY_PHONE]: FormStates.DISCLOSURE,
      [FormStates.DISCLOSURE]: FormStates.PERSONAL_INFO,
      [FormStates.PERSONAL_INFO]: FormStates.OCCUPATION,
      [FormStates.OCCUPATION]: FormStates.ADDITIONAL_INFO,
      [FormStates.ADDITIONAL_INFO]: FormStates.ADDITIONAL_INFO_PAGE2,
      [FormStates.ADDITIONAL_INFO_PAGE2]: FormStates.FRAUD_WARNING_FOLLOW_UP,
      [FormStates.FRAUD_WARNING_FOLLOW_UP]: FormStates.KYC_GENERATE_QUESTIONS,
      [FormStates.KYC_GENERATE_QUESTIONS]: FormStates.COMPLETE,
      // Annual refresh is reached only via resume, not the linear wizard.
      [FormStates.KYC_REFRESH]: FormStates.COMPLETE,
    }
    this.reverseTransitions = this.reverse_transitions()
  }

  getCurrentState() {
    return this.currentState
  }

  private setCurrentState(state: StateValue) {
    this.currentState = state
  }

  public next() {
    if (this.currentState !== FormStates.COMPLETE) {
      this.currentState = this.transitions[this.currentState as TransitionState]
    }
  }

  private reverse_transitions = () =>
    Object.entries(this.transitions).reduce(
      (acc: Partial<Record<StateValue, StateValue>>, [from, to]) => {
        const key = from as TransitionState
        acc[to] = key
        return acc
      },
      {}
    )

  public back() {
    const previousState = this.reverseTransitions[this.currentState]
    if (previousState) {
      this.currentState = previousState
    }
  }

  public jumpTo(page: StateValue) {
    this.setCurrentState(page)
  }

  public reset() {
    this.setCurrentState(FormStates.EMAIL)
  }

  public complete() {
    this.setCurrentState(FormStates.COMPLETE)
  }
}

export const FormManager = new FormWizard()

export const CorporateFormStates = {
  CORPINFO: 'CORPINFO',
  CORP_BUS_ADDRESS: 'CORP_BUS_ADDRESS',
  CORP_ADDN_DETAILS: 'CORP_ADDN_DETAILS',
  CORP_DATE_DETAILS: 'CORP_DATE_DETAILS',
  CORPDOCS: 'CORPDOCS',
  COMPLETE: 'COMPLETE',
} as const

export type CorporateStateValue =
  (typeof CorporateFormStates)[keyof typeof CorporateFormStates]

export class CorporateFormWizard {
  private currentState: CorporateStateValue
  private transitions: Record<CorporateStateValue, CorporateStateValue>
  private reverseTransitions: Record<CorporateStateValue, CorporateStateValue>

  constructor() {
    this.currentState = CorporateFormStates.CORPINFO
    this.transitions = {
      [CorporateFormStates.CORPINFO]: CorporateFormStates.CORP_DATE_DETAILS,
      [CorporateFormStates.CORP_DATE_DETAILS]:
        CorporateFormStates.CORP_BUS_ADDRESS,
      [CorporateFormStates.CORP_BUS_ADDRESS]:
        CorporateFormStates.CORP_ADDN_DETAILS,
      [CorporateFormStates.CORP_ADDN_DETAILS]: CorporateFormStates.CORPDOCS,
      [CorporateFormStates.CORPDOCS]: CorporateFormStates.COMPLETE,
      [CorporateFormStates.COMPLETE]: CorporateFormStates.COMPLETE,
    }
    this.reverseTransitions = this.buildReverseTransitions()
  }

  getCurrentState(): CorporateStateValue {
    return this.currentState
  }

  private setCurrentState(state: CorporateStateValue): void {
    this.currentState = state
  }

  public next(): void {
    if (this.currentState !== CorporateFormStates.COMPLETE) {
      this.currentState = this.transitions[this.currentState]
    }
  }

  private buildReverseTransitions(): Record<
    CorporateStateValue,
    CorporateStateValue
  > {
    return Object.entries(this.transitions).reduce(
      (acc: Record<CorporateStateValue, CorporateStateValue>, [from, to]) => {
        if (to !== CorporateFormStates.COMPLETE) {
          acc[to] = from as CorporateStateValue
        }
        return acc
      },
      {} as Record<CorporateStateValue, CorporateStateValue>
    )
  }

  public back(): void {
    const previousState = this.reverseTransitions[this.currentState]
    if (previousState) {
      this.currentState = previousState
    }
  }

  public jumpTo(page: CorporateStateValue): void {
    this.setCurrentState(page)
  }

  public reset(): void {
    this.setCurrentState(CorporateFormStates.CORPINFO)
  }

  public complete(): void {
    this.setCurrentState(CorporateFormStates.COMPLETE)
  }
}
